/**
 * @voltxampere/engine — Reduced-KCL residual metric (Sprint 8b)
 *
 * Agent 3's Sprint 8a review identified a fundamental problem with
 * the naive `||Y·x − rhs||∞` convergence metric: the MNA augmented
 * rows (voltage-source constraints) are re-satisfied to machine
 * precision by every LU solve, so their residual is always ≈ 0. But
 * the augmented rows also carry the rail voltage (e.g. V_SS = −12 V)
 * in `rhs`, which means the vector `||rhs||∞` is dominated by the
 * rail magnitude. Meanwhile the `||Y·x − rhs||∞` residual is
 * dominated by the tiny machine-epsilon leftover from the augmented
 * rows — the actual node-KCL signal (µA-scale currents) hides
 * several orders of magnitude below.
 *
 * The textbook fix (Sprint 8b) is to **project** residual evaluation
 * onto the node-KCL block before taking a norm. Only the `[0,
 * nodeCount)` rows are meaningful convergence signal; the
 * `[nodeCount, size)` augmented block is ignored. With this change,
 * Newton's "am I converging?" check reflects Kirchhoff current law
 * balance at each node, which is what we actually want.
 *
 * This module exports three primitives:
 *
 *  - {@link computeResidual}: returns `f(x) = Y·x − rhs` (full
 *    length), a generic primitive any caller can use.
 *  - {@link normReducedInf}: infinity norm projected onto a subset
 *    of indices (usually `plan.nodeIndexSet`).
 *  - {@link isConvergedReduced}: combined residual + step-size
 *    convergence predicate with strict / relaxed tiers.
 *
 * The tier semantics follow SPECS §3.4:
 *
 *  - `"strict"` → `reltol = 1e-3`, `abstol = 1e-12`. Default.
 *  - `"relaxed"` → `reltol = 1e-2`, `abstol = 1e-9`. Opt-in tier
 *    for DC operating-point extraction on hard-to-converge
 *    topologies (PNP CE saturation-edge fixture).
 *
 * The Newton driver consults `isConvergedReduced` at each iteration;
 * two-tier orchestration (`solver.ts`) retries a failed strict solve
 * with the relaxed predicate against the LAST strict iterate before
 * falling further down the gmin/source/Anderson ladder.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { ContractViolationError } from "@voltxampere/shared";
import { sparseVectorMul } from "@voltxampere/math";
/**
 * SPECS §3.4 tier → tolerance lookup. Two-tier callers flow through
 * this so the strict/relaxed contract is single-sourced.
 */
export function toleranceForTier(tier) {
    if (tier === "strict")
        return { reltol: 1e-3, abstol: 1e-12 };
    // Sprint 8b: relaxed tier is ~10× more permissive on both
    // reltol and abstol. abstol drops from 1e-12 to 1e-9 because
    // the relaxed band is meant for DC OP extraction where sub-
    // picoamp precision is not needed.
    return { reltol: 1e-2, abstol: 1e-9 };
}
/**
 * Compute the MNA residual `f(x) = Y·x − rhs` as a fresh
 * `Float64Array` of length `x.length`. No projection is applied —
 * callers pass the full residual to {@link normReducedInf} or
 * similar for projected norms.
 */
export function computeResidual(Y, x, rhs) {
    if (x.length !== rhs.length) {
        throw new ContractViolationError(`computeResidual: x length ${String(x.length)} ≠ rhs length ${String(rhs.length)}`);
    }
    if (Y.rows !== rhs.length || Y.cols !== x.length) {
        throw new ContractViolationError(`computeResidual: Y shape (${String(Y.rows)}×${String(Y.cols)}) does not match x/rhs length ${String(rhs.length)}`);
    }
    const Yx = sparseVectorMul(Y, x);
    const out = new Float64Array(rhs.length);
    for (let i = 0; i < rhs.length; i++) {
        out[i] = (Yx[i] ?? 0) - (rhs[i] ?? 0);
    }
    return out;
}
/**
 * Infinity norm projected onto a subset of indices. Equivalent to
 *
 *   max(|vector[i]|) for i in indexSet
 *
 * Returns `0` when `indexSet` is empty. Non-finite entries (NaN,
 * ±Infinity) inside the set short-circuit the loop and return
 * `Infinity` — the Newton driver's `isFiniteVector` check downstream
 * will abort the solve; this function does not silently mask poisoned
 * iterates.
 */
export function normReducedInf(vector, indexSet) {
    let max = 0;
    for (const i of indexSet) {
        if (i < 0 || i >= vector.length)
            continue;
        const v = vector[i] ?? 0;
        if (!Number.isFinite(v))
            return Number.POSITIVE_INFINITY;
        const abs = Math.abs(v);
        if (abs > max)
            max = abs;
    }
    return max;
}
/**
 * Sprint 8b convergence predicate — true iff both:
 *
 *  1. Node-KCL residual: `||residual||∞` (reduced to `nodeIndexSet`)
 *     < `reltol · ||x_node||∞ + abstol`. This is the Agent 3 fix
 *     for the augmented-row noise that drowned the KCL signal.
 *  2. Per-element step check: `|deltaX[i]| ≤ reltol · |x[i]| + abstol`
 *     for EVERY index `i`, node and augmented alike. SPECS §3.4
 *     step-based convergence predicate. Augmented rows (branch
 *     currents) carry genuine convergence signal — on a diode cold
 *     start the branch current grows many orders of magnitude as
 *     pnjlim's junction history walks forward, and excluding those
 *     indices from the step check causes premature convergence at
 *     the "BJT off" trivial fixed point.
 *
 * Only (1) is "reduced". (2) checks every index per-element (the
 * legacy `isConverged` semantics from math/tolerance). Sprint 8b's
 * innovation is the residual-based check — the per-element step
 * check stays as the safety net that prevents early convergence on
 * pnjlim-driven nonlinear-element warm-ups.
 *
 * Strict tier default; relaxed tier accepts convergence with
 * ~10× larger residuals AND step tolerances (acceptable for DC OP
 * extraction where downstream AC/transient analyses linearise
 * around the OP and consume an extra percentage point of error
 * without issue).
 */
export function isConvergedReduced(inputs) {
    const { x, residual, deltaX, nodeIndexSet, tier } = inputs;
    if (x.length !== residual.length || x.length !== deltaX.length) {
        throw new ContractViolationError(`isConvergedReduced: x/residual/deltaX length mismatch (x=${String(x.length)}, residual=${String(residual.length)}, deltaX=${String(deltaX.length)})`);
    }
    const { reltol, abstol } = toleranceForTier(tier);
    // (1) Residual on node-KCL rows only — Sprint 8b Agent 3 fix.
    const xNodeNorm = normReducedInf(x, nodeIndexSet);
    const residualNorm = normReducedInf(residual, nodeIndexSet);
    if (residualNorm >= reltol * xNodeNorm + abstol)
        return false;
    // (2) Per-element step check across the FULL iterate — legacy
    // SPECS §3.4 predicate. Augmented rows retain their convergence
    // signal so the diode cold-start warm-up does not prematurely
    // terminate at the trivial fixed point.
    for (let i = 0; i < x.length; i++) {
        const delta = Math.abs(deltaX[i] ?? 0);
        const mag = Math.abs(x[i] ?? 0);
        if (delta > reltol * mag + abstol)
            return false;
    }
    return true;
}
//# sourceMappingURL=residual.js.map